<template>
  <SDialog
    :show-dialog="showDialog"
    :width="450"
    :title="$t('ida.label.welcome', { firstname: joinStore.firstName })"
    @update:show-dialog="emits('update:showDialog', $event)"
    @close="emits('close')"
  >
    <VCardText class="px-6 pb-6 pt-2">
      <p class="font14 text-center mt-0 mb-4">
        {{ $t('ida.p.customerFound') }}
      </p>
      <p class="font14 text-center mb-4">
        {{ $t('ida.p.passwordConfirm') }}
      </p>

      <ObservedForm
        :loading="loading"
        :error-messages="fieldErrors"
        data-testid="join-welcome-back-form"
        @submit="onSubmit"
      >
        <VRow no-gutters class="mb-3">
          <VCol cols="12">
            <PasswordField
              v-model="password"
              name="joinPassword"
              :alternate-rules="SIGN_IN_PASSWORD_RULES"
              data-testid="join-password"
              autocomplete="current-password"
            />
          </VCol>
        </VRow>
        <VRow no-gutters>
          <VCol cols="12">
            <SBtn
              color="primary"
              action
              type="submit"
              block
              data-testid="join-confirm-password"
            >
              {{ $t('input.btn.continue') }}
            </SBtn>
          </VCol>
        </VRow>
        <VRow no-gutters class="mt-6">
          <VCol class="text-center">
            <SBtn
              link-small
              color="linkNew"
              class="text-uppercase font-weight-medium"
              @click="emits('forgot-password')"
            >
              {{ $t('account.link.forgotPassword') }}
            </SBtn>
          </VCol>
        </VRow>
      </ObservedForm>
    </VCardText>
  </SDialog>
</template>

<script lang="ts" setup>
import { SIGN_IN_PASSWORD_RULES } from '~/utils/validation/password'
import ObservedForm from '~/components/base/ObservedForm.vue'
import PasswordField from '~/components/base/fields/PasswordField.vue'
import { useJoinStore } from '~/stores/join'
import { useAuthStore } from '~/stores/auth'

interface Props {
  showDialog: boolean
}

defineProps<Props>()

const emits = defineEmits<{
  (e: 'update:showDialog', show: boolean): void
  (e: 'close' | 'authenticated' | 'forgot-password'): void
}>()

const joinStore = useJoinStore()
const authStore = useAuthStore()
const { processAction, errors, loading } = useAction()
const { ts } = useI18n()

// useAction only fills `errors` from a thrown error. verifyCustomer() does not throw — it
// returns false and puts the message on joinStore.error — so binding `errors` alone left a
// failed verification with an empty error list and no feedback at all. Merge both.
//
// The store message goes through ts(), as the other join components do with $ts: the store
// falls back to the key 'error.somethingWrong' when the API sends no message, and ObservedForm
// renders what it is given verbatim.
const fieldErrors = computed(() => (
  errors.value.length ? errors.value : (joinStore.error ? [ts(joinStore.error)] : [])
))

const password = ref('')

const onSubmit = () => {
  processAction(async () => {
    await authStore.authenticate({
      username: joinStore.session.email,
      password: password.value,
      persist: false,
    }, { skipRedirects: true, skipWishlist: true })
    // verifyCustomer() swallows its own errors and reports the outcome in its return value.
    // Emitting regardless sent a failed verification on to select-offer with no recruit
    // session — and, once the funnel landed, recorded it as a completed step. Staying put is
    // safe here because nothing has been created yet: resubmitting retries the same two calls.
    // See fieldErrors above for why the message needs the store, not `errors`.
    const verified = await joinStore.verifyCustomer({
      username: joinStore.session.email,
      password: password.value,
    })
    if (!verified) { return }
    emits('authenticated')
  })
}
</script>
